import { Children, isValidElement, type ReactNode, type ReactElement } from 'react';
import './Stepper.css';

export interface StepperProps {
  active: number;
  onStepClick?: (step: number) => void;
  orientation?: 'horizontal' | 'vertical';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Allow clicking on completed steps to navigate back. */
  allowNextStepsSelect?: boolean;
  children?: ReactNode;
}

export interface StepProps {
  label?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  completedIcon?: ReactNode;
  state?: 'idle' | 'completed' | 'progress' | 'error';
  children?: ReactNode;
}

function Step({ children }: StepProps): ReactNode { return <>{children}</>; }
Step.displayName = 'Step';

interface StepperComponent extends React.FC<StepperProps> { Step: typeof Step; }

function StepperRoot({ active, onStepClick, orientation = 'horizontal', size = 'sm', allowNextStepsSelect = true, children }: StepperProps): ReactNode {
  const steps = Children.toArray(children).filter((c) => isValidElement(c) && (c.type as { displayName?: string }).displayName === 'Step') as ReactElement<StepProps>[];
  const total = steps.length;

  return (
    <div className={['rr-stepper', `rr-stepper--${orientation}`, `rr-stepper--size-${size}`].filter(Boolean).join(' ')}>
      <div className="rr-stepper__steps" role="list">
        {steps.map((step, i) => {
          const state: StepProps['state'] =
            step.props.state ?? (i < active ? 'completed' : i === active ? 'progress' : 'idle');
          const clickable = onStepClick && (i < active || allowNextStepsSelect);
          return (
            <div key={i} className={`rr-stepper__step rr-stepper__step--${state}`} role="listitem">
              <button
                type="button"
                className="rr-stepper__bullet"
                onClick={clickable ? () => onStepClick!(i) : undefined}
                disabled={!clickable}
                aria-current={state === 'progress' ? 'step' : undefined}
              >
                {state === 'completed' && step.props.completedIcon ? step.props.completedIcon
                  : state === 'completed' ? '✓'
                  : step.props.icon ?? i + 1}
              </button>
              <div className="rr-stepper__meta">
                {step.props.label ? <div className="rr-stepper__label">{step.props.label}</div> : null}
                {step.props.description ? <div className="rr-stepper__description">{step.props.description}</div> : null}
              </div>
              {i < total - 1 ? <div className="rr-stepper__separator" /> : null}
            </div>
          );
        })}
      </div>
      <div className="rr-stepper__content">
        {steps[active]?.props.children}
      </div>
    </div>
  );
}

export const Stepper = StepperRoot as StepperComponent;
Stepper.Step = Step;
